import React, { PropsWithChildren } from 'react';
import BaseModel from '../model/BaseModel';

import { ServiceApi, WhereCondition } from "../service/ServiceApi";
import CacheRecord from '../utils/CacheRecord';
import PromiseQueue, { CancelableItem } from '../utils/PromiseQueue';
import TicketStatusIcon from './TicketStatusIcon';
import TicketStatus from '../model/TicketStatus';

export interface TicketStatusIconAsyncProps<T> {
    ticket_status_id: string;
    style?: React.CSSProperties;
}

interface TicketStatusIconAsyncState {
    icon?: React.ReactNode | null;
}

export default class TicketStatusIconAsync<T extends BaseModel> extends React.Component<PropsWithChildren<TicketStatusIconAsyncProps<T>>, TicketStatusIconAsyncState> {

    static cache: Record<string, CacheRecord<React.ReactNode>> = {};
    static promiseQueue: PromiseQueue<any, React.ReactNode> = new PromiseQueue<any, React.ReactNode>();

    public api: ServiceApi<TicketStatus>;
    private promiseCancel: CancelableItem | null = null;

    constructor(props: TicketStatusIconAsyncProps<T>) {
        super(props);
        this.state = {
            icon: null
        }
        this.api = new ServiceApi<TicketStatus>('ticketStatus');
        this.getIcon = this.getIcon.bind(this);
        this.setIcon = this.setIcon.bind(this);
    }

    componentDidMount() {
        this.promiseCancel = TicketStatusIconAsync.promiseQueue.enqueue(null, this.getIcon, this.setIcon);
    }

    componentWillUnmount() {
        this.promiseCancel?.cancel();
    }

    async getTicketStatus(): Promise<TicketStatus | undefined> {
        const { ticket_status_id } = this.props;
        if (typeof ticket_status_id === 'string' || typeof ticket_status_id === 'number') {
            return await this.api.getById(ticket_status_id);
        }
    }

    async getIcon(): Promise<React.ReactNode> {
        const { ticket_status_id, style } = this.props;
        var name: React.ReactNode = null;

        const _cacheKey = ticket_status_id;

        if (_cacheKey in TicketStatusIconAsync.cache) {
            var cache = TicketStatusIconAsync.cache[_cacheKey];
            const cacheResult = cache.getValue(ticket_status_id);
            if (cacheResult) return cacheResult;
        }

        const item = await this.getTicketStatus();
        if (item) {
            name = <TicketStatusIcon icon={item.icon} style={style} />;
            if (_cacheKey in TicketStatusIconAsync.cache === false) {
                TicketStatusIconAsync.cache[_cacheKey] = new CacheRecord<React.ReactNode>(60 * 1000); //1 minute
            }
            TicketStatusIconAsync.cache[_cacheKey].setValue(ticket_status_id, name);
        }

        return name;
    }

    setIcon(icon: React.ReactNode) {
        this.setState({ icon });
    }

    render() {
        const { icon } = this.state;
        return icon || null;
    }
}